import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { WISDOM_KEEPERS_GALLERY } from "../../data/siteContent";

const GalleryRow = ({ items, duration, reverse }) => {
  const doubled = [...items, ...items];
  return (
    <div className="overflow-hidden">
      <div
        className="flex gap-5 w-max gme-marquee-row"
        style={{
          animationName: reverse ? "gme-marquee-reverse" : "gme-marquee",
          animationDuration: `${duration}s`,
          animationTimingFunction: "linear",
          animationIterationCount: "infinite",
        }}
      >
        {doubled.map((k, i) => (
          <Link key={i} to="/wisdom-keepers" data-testid={`keeper-${i}`} className="shrink-0 w-64 aspect-[4/3] rounded-xl overflow-hidden ring-1 ring-[#C9A24B]/20 hover:ring-[#C9A24B]/55 transition-all hover:-translate-y-1">
            <img src={k.img} alt={k.name} className="w-full h-full object-cover block" loading="lazy" />
          </Link>
        ))}
      </div>
    </div>
  );
};

// Testimonial spotlight — ported from Gary's legacy site.
// Round headshot, oversized serif quote, dot + arrow controls.
// Auto-advances every 8s (paused on hover/focus; disabled under reduced motion).

// The eight client-approved testimonials (§17), in the order supplied.
// Per Parth 2026-08-25: use these; where one runs long, either ease the type size or
// end cleanly after a sentence — never an ellipsis.
//
// Two quotes close on a sentence about the *Global Wisdom Foundation* rather than Gary.
// Those are ended after the Gary sentence, using exactly that licence, which also keeps
// the ecosystem boundary in Task List §3 intact (GWF is a separate property).
// `trimmed: true` marks those so the change is auditable rather than silent.
const SPOTLIGHT_TESTIMONIALS = [
  {
    id: "thomas-moore",
    quote: "These inspiring new modalities for delivering wisdom can help connect our deepest selves to what matters most. A multi-sensory feast for the senses!",
    personName: "Thomas Moore",
    personTitle: "Bestselling Author of Care of the Soul",
    headshot: "/assets/testimonials/t-thomas-moore.jpg",
  },
  {
    id: "deepak-chopra",
    quote: "This new way to experience wisdom can bring your heart, mind and soul into deeper coherence, offering a breakthrough through which you will feel more receptive, connected and alive. Gary Malkin has come up with a new technology for experiencing your Spirit.",
    personName: "Deepak Chopra",
    personTitle: "Bestselling Author and Spiritual Teacher",
    headshot: null,
  },
  {
    id: "marianne-williamson",
    quote: "Gary’s ability to fuse evocative music with life-enhancing wisdom offers soul-stirring experiences that is touching hearts around the world.",
    personName: "Marianne Williamson",
    personTitle: "Bestselling Author, US Presidential Candidate",
    headshot: null,
    trimmed: true,
  },
  {
    id: "foster-gamble",
    quote: "This way of sharing wisdom is both innovative and profoundly touching, making it possible to be experienced with depth and reverence.",
    personName: "Foster Gamble",
    personTitle: "Producer of the Thrive Documentaries",
    headshot: "/assets/testimonials/t-foster-gamble.jpg",
    trimmed: true,
  },
  {
    id: "desmond-tutu",
    quote: "Gary Malkin’s gift, providing musical enhancement to the spoken word during ritual or prayer, is a gift unparalleled. His work provides a healing balm that makes the delivery of wisdom a true blessing.",
    personName: "Archbishop Desmond Tutu",
    personTitle: "Renowned Spiritual Leader, Author",
    headshot: "/assets/wisdomkeepers/desmond.webp",
  },
  {
    id: "jude-currivan",
    quote: "Gary’s body of work and presentations are profound journeys into beauty and unity, leaving people in states of deep belonging.",
    personName: "Jude Currivan",
    personTitle: "PhD · Bestselling Author, Cosmologist, Futurist",
    headshot: "/assets/testimonials/t-jude-currivan.jpg",
  },
  {
    id: "lynne-twist",
    quote: "Gary Malkin is a public speaker, composer, and performer of music who draws his audiences deep into their core. He has an uncanny ability to know what will bring forth a deeper listening from the audience so that hearts can be opened in just the right way. A rare treasure.",
    personName: "Lynne Twist",
    personTitle: "Author, A Committed Life; Co-Founder, Pachamama Alliance",
    headshot: "/assets/testimonials/t-lynne-twist.jpg",
  },
  {
    id: "jim-garrison",
    quote: "In a world fragmented by distractions, the transformances created by Sarah McCrum and Gary Malkin provide a potent antidote. These short films are not just visual experiences—they are catalysts for collective coherence, bringing a deeper sense of unity and presence that will inspire and elevate your team’s dynamics.",
    personName: "Jim Garrison",
    personTitle: "President, Ubiquity University; Host, Humanity Rising",
    headshot: null,
  },
];

// §17 asks for complete thoughts with no clamping, so a long quote can't be cut —
// it has to be set smaller instead. This eases the type down in three steps rather
// than letting the longest quote dictate the size of every other one.
const quoteSizeClass = (quote) => {
  const n = quote.length;
  if (n > 260) return "text-[clamp(1.05rem,1.9vw,1.5rem)]";
  if (n > 160) return "text-[clamp(1.2rem,2.3vw,1.8rem)]";
  return "text-[clamp(1.4rem,2.8vw,2.15rem)]";
};

// Initials stand in where a headshot hasn't been supplied yet, so the ring stays
// composed instead of showing a broken image.
const initialsOf = (name) =>
  name.replace(/^Archbishop\s+/, "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("");

const easeOut = [0.22, 1, 0.36, 1];
const AUTOPLAY_MS = 8000;

export const TestimonialSpotlight = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduce = useReducedMotion();
  const count = SPOTLIGHT_TESTIMONIALS.length;

  useEffect(() => {
    if (count <= 1 || paused || reduce) return;
    const timer = setInterval(() => setIndex((current) => (current + 1) % count), AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [count, paused, reduce]);

  const current = SPOTLIGHT_TESTIMONIALS[index];

  return (
    <section id="voices" data-testid="testimonial-spotlight" className="relative py-24 md:py-32 overflow-hidden">
      {/* night ground glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[36rem] h-[36rem] radial-glow blur-3xl opacity-60" />
      </div>

      <div className="relative max-w-6xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-sm tracking-[0.34em] uppercase text-[#E7CC86]/80 mb-3">Voices &amp; Impact</p>
          <h2 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-cream">
            This Work Resonates Through <span className="text-gold">Many Worlds</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-cream/65 leading-relaxed max-w-xl mx-auto">
            Boardrooms and global stages. Hospice rooms and hospital corridors. Intimate gatherings around the piano. Diverse settings and cultures, all united by our shared humanity.
          </p>
        </div>

        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center mt-8 md:mt-10">
          <div className="gold-hairline max-w-[6rem] mx-auto" />
        </div>

        <div
          data-testid="spotlight-stage"
          className="grid md:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] gap-10 md:gap-[clamp(2rem,6vw,5rem)] items-center mt-12 md:mt-16"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
        >
          <div className="relative mx-auto md:mx-auto w-[min(15rem,60vw)] aspect-square rounded-full overflow-hidden ring-1 ring-[#C9A24B]/40 shadow-[0_0_0_1px_rgba(201,162,75,0.35),0_8px_28px_rgba(201,162,75,0.28)] bg-[#0B1020]">
            <AnimatePresence mode="wait">
              {current.headshot ? (
                <motion.img
                  key={current.id}
                  src={current.headshot}
                  alt={current.personName}
                  className="absolute inset-0 w-full h-full object-cover"
                  initial={{ opacity: 0, scale: reduce ? 1 : 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: easeOut }}
                />
              ) : (
                <motion.div
                  key={current.id}
                  className="absolute inset-0 grid place-items-center bg-[#0F1526]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: easeOut }}
                  aria-label={current.personName}
                >
                  <span className="font-display text-[3.2rem] tracking-[0.06em] text-[#E7CC86]/70 select-none">
                    {initialsOf(current.personName)}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div>
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={current.id}
                data-testid="spotlight-quote"
                className={`font-display italic ${quoteSizeClass(current.quote)} leading-[1.4] text-cream [text-wrap:balance]`}
                initial={{ opacity: 0, y: reduce ? 0 : 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: reduce ? 0 : -12 }}
                transition={{ duration: 0.5, ease: easeOut }}
              >
                {/* Complete thought always — no clamping, no ellipses, no Read More.
                    The opening quotation mark is integrated with the text so it can't
                    render visually detached from the quote. */}
                <p>“{current.quote}”</p>
              </motion.blockquote>
            </AnimatePresence>

            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, delay: 0.15 }}
              >
                {/* The person's name is the visual anchor; credentials stay secondary. */}
                <p className="mt-6 font-bold text-xl md:text-2xl text-[#E7CC86]">{current.personName}</p>
                {current.personTitle && <p className="mt-1 text-[15px] text-cream/60">{current.personTitle}</p>}
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center justify-center md:justify-start gap-4 mt-10">
              <button
                type="button"
                data-testid="spotlight-prev"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-cream/15 text-cream transition-all duration-300 hover:border-[#C9A24B] hover:text-[#C9A24B] hover:bg-cream/5"
                onClick={() => setIndex((i) => (i - 1 + count) % count)}
                aria-label="Previous testimonial"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M13 8H3m4-4-4 4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </button>

              <div role="tablist" aria-label="Testimonials" className="flex items-center gap-2.5">
                {SPOTLIGHT_TESTIMONIALS.map((testimonial, i) => (
                  <button
                    type="button"
                    key={testimonial.id}
                    role="tab"
                    aria-selected={i === index}
                    aria-label={`Testimonial from ${testimonial.personName}`}
                    data-testid={`spotlight-dot-${i}`}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${i === index ? "bg-[#C9A24B] scale-[1.35]" : "bg-cream/25"}`}
                    onClick={() => setIndex(i)}
                  />
                ))}
              </div>

              <button
                type="button"
                data-testid="spotlight-next"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-cream/15 text-cream transition-all duration-300 hover:border-[#C9A24B] hover:text-[#C9A24B] hover:bg-cream/5"
                onClick={() => setIndex((i) => (i + 1) % count)}
                aria-label="Next testimonial"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M3 8h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Wisdom Keepers framed-photo strip */}
      <div className="relative mt-14">
        <p className="text-center text-xs tracking-[0.24em] uppercase text-cream/40 mb-6">The company the work keeps</p>
        <div className="absolute left-0 top-8 bottom-0 w-24 z-10 bg-gradient-to-r from-[#0B1020] to-transparent pointer-events-none" />
        <div className="absolute right-0 top-8 bottom-0 w-24 z-10 bg-gradient-to-l from-[#0B1020] to-transparent pointer-events-none" />
        <GalleryRow items={WISDOM_KEEPERS_GALLERY} duration={60} reverse />
        <div className="text-center mt-8">
          <Link to="/wisdom-keepers" data-testid="testimonials-wk-link" className="btn-gold inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide">
            See the full gallery <ArrowUpRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};
